import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = {
  income: '#22c55e',
  expenses: '#ef4444',
  loans: '#f59e0b',
  net: '#3b82f6',
};

const CATEGORY_COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#10b981', '#f97316', '#06b6d4'];

const MonthlyAnalysis = ({ transactions, subscriptions, salaryPayments, employees, formatCurrency, balance, quarterlySummary }) => {
  const [periodType, setPeriodType] = useState('monthly'); // 'monthly' or 'quarterly'
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`;
  });
  const [viewMode, setViewMode] = useState('overview'); // overview, spending, forecast, comparison

  const formatCompactNumber = (number) => {
    if (number === 0) return '0';
    return Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(number);
  };

  const CustomTooltip = ({ active, payload, label, formatCurrency }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'var(--primary-bg)',
          border: '1px solid var(--border)',
          padding: '10px',
          borderRadius: '6px'
        }}>
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Get available months from transactions
  const availableMonths = useMemo(() => {
    const months = new Set();
    transactions.forEach(tx => {
      const date = new Date(tx.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.add(monthKey);
    });
    return Array.from(months).sort().reverse();
  }, [transactions]);

  // Get available quarters from transactions
  const availableQuarters = useMemo(() => {
    const quarters = new Set();
    transactions.forEach(tx => {
      const date = new Date(tx.date);
      const year = date.getFullYear();
      const q = Math.floor(date.getMonth() / 3) + 1;
      quarters.add(`${year}-Q${q}`);
    });
    return Array.from(quarters).sort().reverse();
  }, [transactions]);

  // Calculate analysis data
  const analysisData = useMemo(() => {
    let periodTransactions = [];
    let periodStart, periodEnd;

    if (periodType === 'monthly') {
      const [year, month] = selectedMonth.split('-').map(Number);
      periodStart = new Date(year, month - 1, 1);
      periodEnd = new Date(year, month, 0);
    } else {
      const [year, qPart] = selectedQuarter.split('-Q');
      const q = Number(qPart);
      periodStart = new Date(Number(year), (q - 1) * 3, 1);
      periodEnd = new Date(Number(year), q * 3, 0);
    }

    periodTransactions = transactions.filter(tx => {
      const txDate = new Date(tx.date);
      return txDate >= periodStart && txDate <= periodEnd;
    });

    const income = periodTransactions
      .filter(tx => tx.type === 'income' || tx.type === 'loan_repayment')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

    const expenses = periodTransactions
      .filter(tx => tx.type === 'expense' || tx.type === 'loan_taken')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

    const loans = periodTransactions
      .filter(tx => tx.type === 'loan_taken' || tx.type === 'loan_repayment')
      .reduce((sum, tx) => {
        const amt = Number(tx.amount) || 0;
        return tx.type === 'loan_taken' ? sum + amt : sum - amt;
      }, 0);

    // Category breakdown
    const categoryMap = {};
    periodTransactions
      .filter(tx => tx.type === 'expense' || tx.type === 'loan_taken')
      .forEach(tx => {
        const cat = tx.category || (tx.type === 'loan_taken' ? 'Loans' : 'Uncategorized');
        categoryMap[cat] = (categoryMap[cat] || 0) + (Number(tx.amount) || 0);
      });

    const categories = Object.entries(categoryMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Timeline spending pattern (by day for month, by month for quarter)
    const timelineData = [];
    if (periodType === 'monthly') {
      for (let i = 1; i <= periodEnd.getDate(); i++) {
        timelineData.push({ label: i, amount: 0 });
      }
      periodTransactions
        .filter(tx => tx.type === 'expense' || tx.type === 'loan_taken')
        .forEach(tx => {
          const day = new Date(tx.date).getDate();
          if (timelineData[day - 1]) {
            timelineData[day - 1].amount += (Number(tx.amount) || 0);
          }
        });
    } else {
      // By month for quarter
      for (let i = 0; i < 3; i++) {
        const m = periodStart.getMonth() + i;
        const monthLabel = new Date(periodStart.getFullYear(), m, 1).toLocaleDateString('en-US', { month: 'short' });
        timelineData.push({ label: monthLabel, amount: 0, mIndex: m });
      }
      periodTransactions
        .filter(tx => tx.type === 'expense' || tx.type === 'loan_taken')
        .forEach(tx => {
          const txMonth = new Date(tx.date).getMonth();
          const item = timelineData.find(d => d.mIndex === txMonth);
          if (item) item.amount += (Number(tx.amount) || 0);
        });
    }

    // Weekend vs Weekday analysis
    const dayOfWeekSpending = {
      Weekday: 0,
      Weekend: 0
    };
    periodTransactions
      .filter(tx => tx.type === 'expense' || tx.type === 'loan_taken')
      .forEach(tx => {
        const day = new Date(tx.date).getDay();
        const isWeekend = (day === 0 || day === 6);
        dayOfWeekSpending[isWeekend ? 'Weekend' : 'Weekday'] += (Number(tx.amount) || 0);
      });

    // Bank-wise breakdown
    const bankMap = {};
    periodTransactions.forEach(tx => {
      const bank = tx.bankId || 'Unknown';
      if (!bankMap[bank]) {
        bankMap[bank] = { income: 0, expenses: 0 };
      }
      if (tx.type === 'income' || tx.type === 'loan_repayment') {
        bankMap[bank].income += Number(tx.amount) || 0;
      } else if (tx.type === 'expense' || tx.type === 'loan_taken') {
        bankMap[bank].expenses += Number(tx.amount) || 0;
      }
    });

    const netSavings = income - expenses;
    const savingsRate = income > 0 ? (netSavings / income) * 100 : 0;
    const daysInPeriod = (periodEnd - periodStart) / (1000 * 60 * 60 * 24);
    const avgDailySpending = expenses / daysInPeriod;
    const transactionCount = periodTransactions.length;
    const expenseTransactions = periodTransactions.filter(tx => tx.type === 'expense' || tx.type === 'loan_taken');
    const avgTransactionSize = expenseTransactions.length > 0 ? expenses / expenseTransactions.length : 0;

    return {
      income,
      expenses,
      loans,
      netSavings,
      savingsRate,
      categories,
      categoryMap,
      timelineData,
      dayOfWeekSpending,
      bankBreakdown: bankMap,
      avgDailySpending,
      transactionCount,
      avgTransactionSize,
      periodTransactions
    };
  }, [transactions, selectedMonth, selectedQuarter, periodType]);

  // Calculate comparison with previous period
  const comparison = useMemo(() => {
    let prevStart, prevEnd;

    if (periodType === 'monthly') {
      const [year, month] = selectedMonth.split('-').map(Number);
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      prevStart = new Date(prevYear, prevMonth - 1, 1);
      prevEnd = new Date(prevYear, prevMonth, 0);
    } else {
      const [yearStr, qPart] = selectedQuarter.split('-Q');
      const year = Number(yearStr);
      const q = Number(qPart);
      const prevQ = q === 1 ? 4 : q - 1;
      const prevY = q === 1 ? year - 1 : year;
      prevStart = new Date(prevY, (prevQ - 1) * 3, 1);
      prevEnd = new Date(prevY, prevQ * 3, 0);
    }

    const prevTransactions = transactions.filter(tx => {
      const txDate = new Date(tx.date);
      return txDate >= prevStart && txDate <= prevEnd;
    });

    const prevIncome = prevTransactions
      .filter(tx => tx.type === 'income' || tx.type === 'loan_repayment')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

    const prevExpenses = prevTransactions
      .filter(tx => tx.type === 'expense' || tx.type === 'loan_taken')
      .reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

    const prevCategoryMap = {};
    prevTransactions
      .filter(tx => tx.type === 'expense' || tx.type === 'loan_taken')
      .forEach(tx => {
        const cat = tx.category || (tx.type === 'loan_taken' ? 'Loans' : 'Uncategorized');
        prevCategoryMap[cat] = (prevCategoryMap[cat] || 0) + (Number(tx.amount) || 0);
      });

    const incomeChange = prevIncome > 0 ? ((analysisData.income - prevIncome) / prevIncome) * 100 : 0;
    const expenseChange = prevExpenses > 0 ? ((analysisData.expenses - prevExpenses) / prevExpenses) * 100 : 0;

    // Category-wise MoM/QoQ
    const categoryChanges = Object.keys({ ...analysisData.categoryMap, ...prevCategoryMap }).map(cat => {
      const current = analysisData.categoryMap[cat] || 0;
      const prev = prevCategoryMap[cat] || 0;
      const change = prev > 0 ? ((current - prev) / prev) * 100 : current > 0 ? 100 : 0;
      return { name: cat, current, prev, change };
    }).sort((a, b) => Math.abs(b.change) - Math.abs(a.change));

    return {
      prevIncome,
      prevExpenses,
      incomeChange,
      expenseChange,
      categoryChanges
    };
  }, [transactions, selectedMonth, selectedQuarter, periodType, analysisData]);

  // Forecast next period
  const forecast = useMemo(() => {
    // For simplicity, fixed expenses are same in forecast (monthly * 3 if quarterly)
    const factor = periodType === 'monthly' ? 1 : 3;

    // Average from last 3 periods
    let avgIncome = 0;
    let avgExpenses = 0;

    if (periodType === 'monthly') {
      const last3Months = [];
      const [year, month] = selectedMonth.split('-').map(Number);
      for (let i = 0; i < 3; i++) {
        let m = month - i;
        let y = year;
        while (m <= 0) { m += 12; y -= 1; }
        last3Months.push({ y, m });
      }
      const sumIncome = last3Months.reduce((sum, p) => {
        const start = new Date(p.y, p.m - 1, 1);
        const end = new Date(p.y, p.m, 0);
        return sum + transactions.filter(tx => {
          const d = new Date(tx.date);
          return d >= start && d <= end && (tx.type === 'income' || tx.type === 'loan_repayment');
        }).reduce((s, tx) => s + Number(tx.amount), 0);
      }, 0);
      avgIncome = sumIncome / 3;
      avgExpenses = last3Months.reduce((sum, p) => {
        const start = new Date(p.y, p.m - 1, 1);
        const end = new Date(p.y, p.m, 0);
        return sum + transactions.filter(tx => {
          const d = new Date(tx.date);
          return d >= start && d <= end && (tx.type === 'expense' || tx.type === 'loan_taken');
        }).reduce((s, tx) => s + Number(tx.amount), 0);
      }, 0) / 3;
    } else {
      // Last 3 quarters
      const last3Quarters = [];
      const [year, qPart] = selectedQuarter.split('-Q');
      const q = Number(qPart);
      const y = Number(year);
      for (let i = 0; i < 3; i++) {
        let curQ = q - i;
        let curY = y;
        while (curQ <= 0) { curQ += 4; curY -= 1; }
        last3Quarters.push({ y: curY, q: curQ });
      }
      avgIncome = last3Quarters.reduce((sum, p) => {
        const start = new Date(p.y, (p.q - 1) * 3, 1);
        const end = new Date(p.y, p.q * 3, 0);
        return sum + transactions.filter(tx => {
          const d = new Date(tx.date);
          return d >= start && d <= end && (tx.type === 'income' || tx.type === 'loan_repayment');
        }).reduce((s, tx) => s + Number(tx.amount), 0);
      }, 0) / 3;
      avgExpenses = last3Quarters.reduce((sum, p) => {
        const start = new Date(p.y, (p.q - 1) * 3, 1);
        const end = new Date(p.y, p.q * 3, 0);
        return sum + transactions.filter(tx => {
          const d = new Date(tx.date);
          return d >= start && d <= end && (tx.type === 'expense' || tx.type === 'loan_taken');
        }).reduce((s, tx) => s + Number(tx.amount), 0);
      }, 0) / 3;
    }

    const upcomingSubs = subscriptions.reduce((sum, sub) => sum + (Number(sub.amount) || 0), 0) * factor;
    const upcomingSalaries = employees.reduce((sum, emp) => sum + (Number(emp.monthlySalary) || 0), 0) * factor;

    const projectedExpenses = avgExpenses + upcomingSubs + upcomingSalaries;
    const projectedSavings = avgIncome - projectedExpenses;
    const burnRateValue = avgExpenses > 0 ? avgExpenses : 1;
    const runwayNum = balance / (burnRateValue / factor); // Runway in periods

    return {
      projectedIncome: avgIncome,
      projectedExpenses,
      projectedSavings,
      upcomingSubs,
      upcomingSalaries,
      burnRate: burnRateValue,
      runway: runwayNum
    };
  }, [transactions, subscriptions, employees, selectedMonth, selectedQuarter, periodType, balance]);

  const getTrendIcon = (value) => {
    if (value > 5) return '↗️';
    if (value < -5) return '↘️';
    return '→';
  };

  const getTrendColor = (value, isExpense = false) => {
    if (isExpense) {
      if (value < -5) return 'text-green';
      if (value > 5) return 'text-red';
    } else {
      if (value > 5) return 'text-green';
      if (value < -5) return 'text-red';
    }
    return 'text-gray';
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="var(--text-primary)"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${name} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };

  return (
    <div className="fade-in">
      <div className="flex justify-between items-center mb-6 flex-mobile-col">
        <h2 className="text-2xl font-bold text-yellow">📅 {periodType === 'monthly' ? 'Monthly' : 'Quarterly'} Analysis</h2>
        <div className="flex gap-2">
          <div className="flex bg-secondary p-1 rounded-lg mr-2">
            <button
              onClick={() => setPeriodType('monthly')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${periodType === 'monthly' ? 'bg-primary text-yellow shadow-lg' : 'text-muted hover:text-white'}`}
            >
              MONTHLY
            </button>
            <button
              onClick={() => setPeriodType('quarterly')}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${periodType === 'quarterly' ? 'bg-primary text-yellow shadow-lg' : 'text-muted hover:text-white'}`}
            >
              QUARTERLY
            </button>
          </div>
          {periodType === 'monthly' ? (
            <select
              className="form-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{ padding: '0.5rem 1rem' }}
            >
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </option>
              ))}
            </select>
          ) : (
            <select
              className="form-select"
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
              style={{ padding: '0.5rem 1rem' }}
            >
              {availableQuarters.map(q => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setViewMode('overview')}
          className={`btn ${viewMode === 'overview' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
        >
          📊 Overview
        </button>
        <button
          onClick={() => setViewMode('spending')}
          className={`btn ${viewMode === 'spending' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
        >
          💸 Spending Patterns
        </button>
        <button
          onClick={() => setViewMode('forecast')}
          className={`btn ${viewMode === 'forecast' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
        >
          🔮 Forecast
        </button>
        <button
          onClick={() => setViewMode('comparison')}
          className={`btn ${viewMode === 'comparison' ? 'btn-primary' : 'btn-secondary'}`}
          style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
        >
          📈 Comparison
        </button>
      </div>

      {/* Overview Mode */}
      {viewMode === 'overview' && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="stat-card success">
              <p className="text-xs text-gray mb-1">💰 Income</p>
              <p className="text-xl font-bold text-green">{formatCurrency(analysisData.income)}</p>
              <p className={`text-xs mt-1 ${getTrendColor(comparison.incomeChange)}`}>
                {getTrendIcon(comparison.incomeChange)} {comparison.incomeChange.toFixed(1)}% {periodType === 'monthly' ? 'MoM' : 'QoQ'}
              </p>
            </div>

            <div className="stat-card" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <p className="text-xs text-gray mb-1">💸 Expenses</p>
              <p className="text-xl font-bold text-red">{formatCurrency(analysisData.expenses)}</p>
              <p className={`text-xs mt-1 ${getTrendColor(comparison.expenseChange, true)}`}>
                {getTrendIcon(comparison.expenseChange)} {comparison.expenseChange.toFixed(1)}% {periodType === 'monthly' ? 'MoM' : 'QoQ'}
              </p>
            </div>

            <div className="stat-card" style={{
              background: analysisData.netSavings >= 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${analysisData.netSavings >= 0 ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`
            }}>
              <p className="text-xs text-gray mb-1">💵 Net Savings</p>
              <p className={`text-xl font-bold ${analysisData.netSavings >= 0 ? 'text-green' : 'text-red'}`}>
                {formatCurrency(analysisData.netSavings)}
              </p>
              <p className="text-xs text-muted mt-1">{analysisData.savingsRate.toFixed(1)}% rate</p>
            </div>

            <div className="stat-card" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
              <p className="text-xs text-gray mb-1">📊 Transactions</p>
              <p className="text-xl font-bold text-blue">{analysisData.transactionCount}</p>
              <p className="text-xs text-muted mt-1">Avg: {formatCurrency(analysisData.avgTransactionSize)}</p>
            </div>
          </div>

          {/* Advanced Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="card" style={{ borderLeft: '4px solid var(--accent-yellow)' }}>
              <h3 className="text-sm font-bold text-muted uppercase mb-2">🏃‍♂️ Financial Runway</h3>
              <p className="text-3xl font-bold text-yellow">{forecast.runway.toFixed(1)} <span className="text-sm text-muted">{periodType === 'monthly' ? 'Months' : 'Quarters'}</span></p>
              <p className="text-xs text-muted mt-2">Based on current balance and 3-period avg burn rate ({formatCurrency(forecast.burnRate)}/{periodType === 'monthly' ? 'mo' : 'qtr'})</p>
            </div>
            <div className="card" style={{ borderLeft: '4px solid #3b82f6' }}>
              <h3 className="text-sm font-bold text-muted uppercase mb-2">⚖️ Income/Expense Ratio</h3>
              <p className="text-3xl font-bold text-blue">{(analysisData.income / (analysisData.expenses || 1)).toFixed(2)}x</p>
              <p className="text-xs text-muted mt-2">{analysisData.income >= analysisData.expenses ? 'Surplus' : 'Deficit'} position this {periodType === 'monthly' ? 'month' : 'quarter'}</p>
            </div>
            <div className="card" style={{ borderLeft: '4px solid #10b981' }}>
              <h3 className="text-sm font-bold text-muted uppercase mb-2">📈 Growth Score</h3>
              <p className="text-3xl font-bold text-green">{Math.min(100, Math.max(0, (analysisData.savingsRate + comparison.incomeChange) / 2 + 50)).toFixed(0)}/100</p>
              <p className="text-xs text-muted mt-2">Combined metric of savings rate and income growth</p>
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="card">
              <h3 className="text-lg font-bold mb-4">📋 Top {periodType === 'monthly' ? 'Monthly' : 'Quarterly'} Categories</h3>
              <div className="space-y-3">
                {analysisData.categories.slice(0, 5).map((cat, index) => {
                  const percentage = analysisData.expenses > 0 ? (cat.value / analysisData.expenses) * 100 : 0;
                  return (
                    <div key={index}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-semibold text-sm">{cat.name}</span>
                        <span className="text-red font-bold text-sm">
                          {formatCurrency(cat.value)} ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: 'var(--secondary-bg)',
                        borderRadius: '3px'
                      }}>
                        <div style={{
                          width: `${percentage}%`,
                          height: '100%',
                          backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
                          borderRadius: '3px'
                        }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-bold mb-4">🏦 Bank Account Activity</h3>
              <div className="space-y-3">
                {Object.entries(analysisData.bankBreakdown).map(([bank, data], index) => (
                  <div key={index} className="p-3" style={{ background: 'var(--secondary-bg)', borderRadius: '6px' }}>
                    <p className="font-semibold mb-2">{bank.charAt(0).toUpperCase() + bank.slice(1)}</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-green">↓ {formatCurrency(data.income)}</span>
                      <span className="text-red">↑ {formatCurrency(data.expenses)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Spending Patterns Mode */}
      {viewMode === 'spending' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            <div className="stat-card">
              <p className="text-xs text-gray mb-1">📅 Avg Daily Spending</p>
              <p className="text-xl font-bold text-orange">{formatCurrency(analysisData.avgDailySpending)}</p>
            </div>
            <div className="stat-card">
              <p className="text-xs text-gray mb-1">🔢 Avg Transaction</p>
              <p className="text-xl font-bold text-blue">{formatCurrency(analysisData.avgTransactionSize)}</p>
            </div>
            <div className="stat-card">
              <p className="text-xs text-gray mb-1">📊 Total Transactions</p>
              <p className="text-xl font-bold text-yellow">{analysisData.transactionCount}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="card">
              <h3 className="text-lg font-bold mb-4">📈 {periodType === 'monthly' ? 'Daily' : 'Monthly'} Spending Pattern</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analysisData.timelineData} margin={{ bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke="var(--text-primary)"
                    label={{ value: periodType === 'monthly' ? 'Day of Month' : 'Month', position: 'insideBottom', offset: -10, fill: 'var(--text-muted)' }}
                  />
                  <YAxis stroke="var(--text-primary)" tickFormatter={formatCompactNumber} />
                  <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                  <Bar dataKey="amount" fill={COLORS.expenses} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <h3 className="text-lg font-bold mb-4">🗓️ Weekend vs Weekday</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(analysisData.dayOfWeekSpending).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    <Cell fill="#3b82f6" />
                    <Cell fill="#f59e0b" />
                  </Pie>
                  <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="text-center mt-2">
                <p className="text-xs text-muted">
                  You spend <strong>{((analysisData.dayOfWeekSpending.Weekend / (analysisData.expenses || 1)) * 100).toFixed(1)}%</strong> on weekends
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-bold mb-4">🥧 Category Distribution</h3>
            <div style={{ width: '100%', height: 400 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={analysisData.categories.slice(0, 8)}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={renderCustomizedLabel}
                    outerRadius={120}
                    innerRadius={60}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {analysisData.categories.slice(0, 8).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Forecast Mode */}
      {viewMode === 'forecast' && (
        <>
          <div className="card mb-6" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
            <h3 className="text-xl font-bold mb-4">🔮 Next {periodType === 'monthly' ? 'Month' : 'Quarter'} Projection</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray mb-1">Projected Income</p>
                <p className="text-2xl font-bold text-green">{formatCurrency(forecast.projectedIncome)}</p>
                <p className="text-xs text-muted">Based on 3-period avg</p>
              </div>
              <div>
                <p className="text-sm text-gray mb-1">Projected Expenses</p>
                <p className="text-2xl font-bold text-red">{formatCurrency(forecast.projectedExpenses)}</p>
                <p className="text-xs text-muted">Including fixed costs</p>
              </div>
              <div>
                <p className="text-sm text-gray mb-1">Projected Savings</p>
                <p className={`text-2xl font-bold ${forecast.projectedSavings >= 0 ? 'text-green' : 'text-red'}`}>
                  {formatCurrency(forecast.projectedSavings)}
                </p>
                <p className="text-xs text-muted">
                  {forecast.projectedIncome > 0 ? ((forecast.projectedSavings / forecast.projectedIncome) * 100).toFixed(1) : 0}% rate
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="card">
              <h3 className="text-lg font-bold mb-4">📋 Fixed Period Costs</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3" style={{ background: 'var(--secondary-bg)', borderRadius: '6px' }}>
                  <span>💳 Subscriptions</span>
                  <span className="font-bold text-orange">{formatCurrency(forecast.upcomingSubs)}</span>
                </div>
                <div className="flex justify-between items-center p-3" style={{ background: 'var(--secondary-bg)', borderRadius: '6px' }}>
                  <span>👥 Salaries</span>
                  <span className="font-bold text-orange">{formatCurrency(forecast.upcomingSalaries)}</span>
                </div>
                <div className="flex justify-between items-center p-3 border-t-2" style={{ borderColor: 'var(--border)' }}>
                  <span className="font-semibold">Total Period Fixed</span>
                  <span className="text-lg font-bold text-red">
                    {formatCurrency(forecast.upcomingSubs + forecast.upcomingSalaries)}
                  </span>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-bold mb-4">📊 {periodType === 'monthly' ? 'Runway' : 'Quarters'} Projection</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Current Runway</span>
                  <span className="font-bold text-yellow">{forecast.runway.toFixed(1)} {periodType === 'monthly' ? 'Months' : 'Quarters'}</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.min(100, (forecast.runway / (periodType === 'monthly' ? 12 : 4)) * 100)}%`,
                      backgroundColor: forecast.runway > (periodType === 'monthly' ? 6 : 2) ? '#22c55e' : forecast.runway > (periodType === 'monthly' ? 3 : 1) ? '#f59e0b' : '#ef4444'
                    }}
                  ></div>
                </div>
                <p className="text-xs text-muted">
                  {forecast.runway > (periodType === 'monthly' ? 6 : 2) ? '✅ Healthy runway.' :
                    forecast.runway > (periodType === 'monthly' ? 3 : 1) ? '⚠️ Moderate runway.' :
                      '🚨 Critical runway focus needed.'}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Comparison Mode */}
      {viewMode === 'comparison' && (
        <>
          <div className="card mb-6">
            <h3 className="text-lg font-bold mb-4">📊 {periodType === 'monthly' ? 'Month-over-Month' : 'Quarter-over-Quarter'} Comparison</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray mb-2">Income Trend</p>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-xs text-muted">Previous</p>
                    <p className="text-lg font-bold">{formatCurrency(comparison.prevIncome)}</p>
                  </div>
                  <div className="text-2xl">{getTrendIcon(comparison.incomeChange)}</div>
                  <div>
                    <p className="text-xs text-muted">Current</p>
                    <p className="text-lg font-bold text-green">{formatCurrency(analysisData.income)}</p>
                  </div>
                </div>
                <p className={`text-sm mt-2 ${getTrendColor(comparison.incomeChange)}`}>
                  {comparison.incomeChange >= 0 ? '+' : ''}{comparison.incomeChange.toFixed(1)}% change
                </p>
              </div>

              <div>
                <p className="text-sm text-gray mb-2">Expense Trend</p>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-xs text-muted">Previous</p>
                    <p className="text-lg font-bold">{formatCurrency(comparison.prevExpenses)}</p>
                  </div>
                  <div className="text-2xl">{getTrendIcon(comparison.expenseChange)}</div>
                  <div>
                    <p className="text-xs text-muted">Current</p>
                    <p className="text-lg font-bold text-red">{formatCurrency(analysisData.expenses)}</p>
                  </div>
                </div>
                <p className={`text-sm mt-2 ${getTrendColor(comparison.expenseChange, true)}`}>
                  {comparison.expenseChange >= 0 ? '+' : ''}{comparison.expenseChange.toFixed(1)}% change
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="card">
              <h3 className="text-lg font-bold mb-4">🔄 Category Shifts</h3>
              <div className="space-y-3">
                {comparison.categoryChanges.slice(0, 6).map((cat, index) => (
                  <div key={index} className="flex justify-between items-center p-2" style={{ background: 'var(--secondary-bg)', borderRadius: '6px' }}>
                    <div>
                      <p className="text-sm font-semibold">{cat.name}</p>
                      <p className="text-xs text-muted">{formatCurrency(cat.prev)} → {formatCurrency(cat.current)}</p>
                    </div>
                    <div className={`text-sm font-bold ${cat.change > 0 ? 'text-red' : 'text-green'}`}>
                      {cat.change > 0 ? '↑' : '↓'} {Math.abs(cat.change).toFixed(1)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 className="text-lg font-bold mb-4">📈 Performance Summary</h3>
              <div className="space-y-3">
                <div className="p-3" style={{ background: 'var(--secondary-bg)', borderRadius: '6px' }}>
                  <div className="flex justify-between items-center">
                    <span>Savings Rate</span>
                    <span className="font-bold">
                      {analysisData.savingsRate.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="p-3" style={{ background: 'var(--secondary-bg)', borderRadius: '6px' }}>
                  <div className="flex justify-between items-center">
                    <span>Expense Ratio</span>
                    <span className="font-bold">
                      {analysisData.income > 0 ? ((analysisData.expenses / analysisData.income) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
                <div className="p-3" style={{ background: 'var(--secondary-bg)', borderRadius: '6px' }}>
                  <div className="flex justify-between items-center">
                    <span>Income Growth</span>
                    <span className={`font-bold ${getTrendColor(comparison.incomeChange)}`}>
                      {comparison.incomeChange >= 0 ? '+' : ''}{comparison.incomeChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthlyAnalysis;